"use client"

import * as React from 'react'
import Link from 'next/link'
import type { InjectionWidgetComponentProps } from '@open-mercato/shared/modules/widgets/injection'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { Button } from '@open-mercato/ui/primitives/button'
import { Badge } from '@open-mercato/ui/primitives/badge'
import {
  formatParticipantTypeLabel,
  formatSupportedModeLabel,
  formatSupportedSegmentLabel,
} from '../../../lib/participantProfile'

type ParticipantSummary = {
  id: string
  participantType: string | null
  supportedModes: string[]
  supportedSegments: string[]
  nip: string | null
  vatEu: string | null
  eori: string | null
  isVerified: boolean
}

type ParticipantsResponse = {
  items?: Array<Record<string, unknown>>
}

function normalizeText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
}

function readCompanyId(context: unknown, data: unknown): string | null {
  const ctx = context && typeof context === 'object' ? context as Record<string, unknown> : {}
  const payload = data && typeof data === 'object' ? data as Record<string, unknown> : {}
  const company = payload.company && typeof payload.company === 'object'
    ? payload.company as Record<string, unknown>
    : {}
  return normalizeText(ctx.companyId)
    ?? normalizeText(ctx.resourceId)
    ?? normalizeText(company.id)
}

function mapParticipantSummary(item: Record<string, unknown>): ParticipantSummary | null {
  const id = normalizeText(item.id)
  if (!id) return null
  return {
    id,
    participantType: normalizeText(item.participantType),
    supportedModes: normalizeStringArray(item.supportedModes),
    supportedSegments: normalizeStringArray(item.supportedSegments),
    nip: normalizeText(item.nip),
    vatEu: normalizeText(item.vatEu),
    eori: normalizeText(item.eori),
    isVerified: item.isVerified === true,
  }
}

export default function CustomerParticipantSummaryWidget({ context, data }: InjectionWidgetComponentProps) {
  const t = useT()
  const companyId = React.useMemo(() => readCompanyId(context, data), [context, data])
  const [participant, setParticipant] = React.useState<ParticipantSummary | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false

    async function load() {
      if (!companyId) {
        if (!cancelled) {
          setParticipant(null)
          setLoading(false)
          setError(null)
        }
        return
      }

      setLoading(true)
      setError(null)
      try {
        const payload = await readApiResultOrThrow<ParticipantsResponse>(
          `/api/participants?customerEntityId=${encodeURIComponent(companyId)}&page=1&pageSize=1`,
          undefined,
          { errorMessage: t('participants.widget.loadError', 'Failed to load freight profile.') },
        )
        if (cancelled) return
        const first = Array.isArray(payload.items) && payload.items.length > 0
          ? mapParticipantSummary(payload.items[0] as Record<string, unknown>)
          : null
        setParticipant(first)
      } catch (loadError) {
        if (cancelled) return
        const message = loadError instanceof Error && loadError.message
          ? loadError.message
          : t('participants.widget.loadError', 'Failed to load freight profile.')
        setError(message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => { cancelled = true }
  }, [companyId, t])

  if (!companyId) return null

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{t('participants.widget.title', 'Freight participant')}</div>
          <div className="text-xs text-muted-foreground">
            {t('participants.widget.description', 'Freight profile linked through the participants extension module.')}
          </div>
        </div>
        {participant ? (
          <Button asChild size="sm" variant="outline">
            <Link href={`/backend/participants/${participant.id}`}>
              {t('participants.widget.actions.open', 'Open profile')}
            </Link>
          </Button>
        ) : (
          <Button asChild size="sm">
            <Link href={`/backend/participants/create?customerEntityId=${encodeURIComponent(companyId)}`}>
              {t('participants.widget.actions.create', 'Create profile')}
            </Link>
          </Button>
        )}
      </div>

      {loading ? <div className="text-xs text-muted-foreground">{t('participants.widget.loading', 'Loading freight profile…')}</div> : null}
      {!loading && error ? <div className="text-xs text-destructive">{error}</div> : null}

      {!loading && !error && participant ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {participant.participantType ? <Badge variant="secondary">{formatParticipantTypeLabel(participant.participantType)}</Badge> : null}
            <Badge variant={participant.isVerified ? 'default' : 'outline'}>
              {participant.isVerified ? t('participants.widget.verified', 'Verified') : t('participants.widget.unverified', 'Pending verification')}
            </Badge>
          </div>

          <div className="space-y-1 text-xs">
            <div className="font-medium text-foreground">{t('participants.widget.modes', 'Modes')}</div>
            <div className="flex flex-wrap gap-1">
              {participant.supportedModes.length > 0 ? participant.supportedModes.map((mode) => (
                <Badge key={mode} variant="outline">{formatSupportedModeLabel(mode)}</Badge>
              )) : <span className="text-muted-foreground">—</span>}
            </div>
          </div>

          <div className="space-y-1 text-xs">
            <div className="font-medium text-foreground">{t('participants.widget.segments', 'Segments')}</div>
            <div className="flex flex-wrap gap-1">
              {participant.supportedSegments.length > 0 ? participant.supportedSegments.slice(0, 4).map((segment) => (
                <Badge key={segment} variant="outline">{formatSupportedSegmentLabel(segment)}</Badge>
              )) : <span className="text-muted-foreground">—</span>}
            </div>
          </div>

          <div className="grid gap-2 text-xs min-[480px]:grid-cols-3">
            <div>
              <div className="font-medium text-foreground">NIP</div>
              <div className="text-muted-foreground">{participant.nip ?? '—'}</div>
            </div>
            <div>
              <div className="font-medium text-foreground">VAT-EU</div>
              <div className="text-muted-foreground">{participant.vatEu ?? '—'}</div>
            </div>
            <div>
              <div className="font-medium text-foreground">EORI</div>
              <div className="text-muted-foreground">{participant.eori ?? '—'}</div>
            </div>
          </div>
        </div>
      ) : null}

      {!loading && !error && !participant ? (
        <div className="text-xs text-muted-foreground">
          {t(
            'participants.widget.empty',
            'No freight profile is linked yet. Create one to keep freight context inside the CRM company record.',
          )}
        </div>
      ) : null}
    </div>
  )
}
